import type { MyTime } from "@/api/types";

/**
 * The last `GET /api/demo/time/mine` the API returned, kept so the punch clock survives
 * a RELOAD with no network — ef-017 AC8, and the same shape as `sessionCache.ts` right
 * beside it (itself modelled on `referenceCache.ts`).
 *
 * `sessionCache.ts` alone gets `App.tsx` past the "who are you" gate; this is the SECOND
 * `/api/*` call the punch clock makes before it can render anything (`useMyTime`'s own
 * `GET /mine`), and it fails offline for the identical reason — `sw.js` never intercepts
 * `/api/*` traffic, on purpose (live data). Without this, a reload with no signal would
 * clear the session gate and then dead-end on `TimeClock.tsx`'s OWN "Can't load your time
 * right now" error state instead.
 *
 * NO EXPIRY, for the same reason referenceCache.ts gives: a snapshot that is a little
 * stale is still the right SHAPE of screen (a real running shift, a real recent-days
 * list) for a worker who cannot reach the server to get anything fresher, and the
 * moment a real network response lands this is overwritten within a second.
 */

const KEY = "easyfarm.mytime.v1";

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function writeMyTimeSnapshot(data: MyTime): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(KEY, JSON.stringify(data));
  } catch {
    // Quota or private mode — degrade to "no cache", never a visible error over this.
  }
}

/** Returns null on anything unexpected rather than throwing — a corrupted entry must
 *  not be worse than an absent one (same discipline as referenceCache.ts). Only a
 *  shape check, not a full schema validation: this is a same-origin, same-device
 *  snapshot of this app's OWN prior response, not untrusted input. */
export function readMyTimeSnapshot(): MyTime | null {
  const store = storage();
  if (!store) return null;
  let raw: string | null;
  try {
    raw = store.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<MyTime>;
    if (parsed.mode !== "punch" && parsed.mode !== "manual" && parsed.mode !== "both") return null;
    if (!Array.isArray(parsed.recent) || !parsed.period) return null;
    return parsed as MyTime;
  } catch {
    return null;
  }
}
